import React, { useState, useContext, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
  Modal,
  Carousel,
} from "react-bootstrap";
import { FaMapMarkerAlt, FaPhoneAlt, FaWhatsapp } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import "./home.css";
import { CartContext } from "./CartContext";
import PaginationComponent from "../components/Forms";
import API_DOMAIN from "../config/config";

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    cart,
    addToCart,
    increaseQty,
    decreaseQty,
    removeItem,
    updateQty,
    orderMode,
    setOrderMode,
    userDetails,
    setUserDetails,
  } = useContext(CartContext);

  // ------------------ MODAL STATE --------------------
  const [tempOrderMode, setTempOrderMode] = useState(null);
  const [tempUserDetails, setTempUserDetails] = useState({
    name: "",
    phone: "",
    address: "",
    tableNo: "",
  });
  const [formError, setFormError] = useState("");
  const [phoneBlurError, setPhoneBlurError] = useState("");

  useEffect(() => {
    if (orderMode !== null) {
      setTempOrderMode(orderMode);
    } else if (location.state?.lastMode !== undefined) {
      setTempOrderMode(location.state.lastMode);
    }
    setTempUserDetails(userDetails);
  }, [orderMode, userDetails, location.state]);

  const handleInputChange = (field, value) => {
    setTempUserDetails((prev) => ({ ...prev, [field]: value }));
  };

  const isFormValid = () => {
    if (tempOrderMode === null) return false;
    if (!tempUserDetails.name || !tempUserDetails.phone) return false;
    if (tempOrderMode === 0 && !tempUserDetails.tableNo) return false;
    if (tempOrderMode === 3 && !tempUserDetails.address) return false;
    return tempUserDetails.phone.length === 10;
  };

  const handleSubmitOrderType = () => {
    if (isFormValid()) {
      setOrderMode(tempOrderMode);
      setUserDetails(tempUserDetails);
    } else {
      setFormError("Please fill all required fields correctly.");
    }
  };

  // ------------------ COMPANY DETAILS STATE --------------------
  const [company, setCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [companyError, setCompanyError] = useState(null);
  // console.log("Company :", company);
  // ------------------ PRODUCTS & CATEGORY STATE ----------------
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [banners, setBanners] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(true);
  const [websiteSettings, setWebsiteSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const itemsPerPage = 100;

  // ------------------ FETCH BANNERS --------------------
  useEffect(() => {
    const fetchBanners = async () => {
      setBannersLoading(true);
      try {
        const response = await fetch(`${API_DOMAIN}/banner.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ search_text: "" }),
        });
        const data = await response.json();
        if (data.head.code === 200) {
          setBanners(data.body.banner || []);
        }
      } catch (err) {
        console.error("Error fetching banners:", err);
      } finally {
        setBannersLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // ------------------ FETCH SETTINGS --------------------
  useEffect(() => {
    const fetchSettings = async () => {
      setSettingsLoading(true);
      try {
        const response = await fetch(`${API_DOMAIN}/website_settings.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "getSettings" }),
        });
        const data = await response.json();
        if (data.head.code === 200) {
          const settings = data.body.settings;
          setWebsiteSettings(settings);

          // If the modal is shown and the current selected temp mode is no longer enabled, reset it
          if (orderMode === null && tempOrderMode !== null) {
            const isTableEnabled = Number(settings.table_order_enabled) === 0;
            const isWalkingEnabled = Number(settings.walking_customer_enabled) === 0;
            const isHomeEnabled = Number(settings.home_delivery_enabled) === 0;

            if (tempOrderMode === 0 && !isTableEnabled) setTempOrderMode(null);
            if (tempOrderMode === 1 && !isWalkingEnabled) setTempOrderMode(null);
            if (tempOrderMode === 3 && !isHomeEnabled) setTempOrderMode(null);
          }
        }
      } catch (err) {
        console.error("Error fetching settings:", err);
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, [orderMode]);
  useEffect(() => {
    const fetchCompany = async () => {
      setCompanyLoading(true);
      setCompanyError(null);

      try {
        const payload = { action: "list" };
        const url = `${API_DOMAIN}/company.php`;

        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error("Failed to fetch company details");

        const data = await response.json();
        console.log(data);
        if (data.status === 200 && data.data && data.data.length > 0) {
          setCompany(data.data[0]);
        } else {
          throw new Error(data.msg || "No company data found");
        }
      } catch (err) {
        setCompanyError(err.message);
      } finally {
        setCompanyLoading(false);
      }
    };

    fetchCompany();
  }, []);

  // ------------------ FETCH PRODUCTS + CATEGORIES --------------
  useEffect(() => {
    const fetchProductsAndCategories = async () => {
      setLoading(true);
      setError(null);

      try {
        // 1. CATEGORY FETCH
        const categoryResponse = await fetch(`${API_DOMAIN}/category.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "listCategory" }),
        });

        const categoryData = await categoryResponse.json();

        let fetchedCategories = [
          { id: "all", name: "All" },
          { id: "discount", name: "Discount Products" }, // Added Discount category
        ];
        if (categoryData.head.code === 200) {
          const apiCats = categoryData.body.categories.map((cat) => ({
            id: cat.category_name,
            name: cat.category_name,
          }));
          fetchedCategories = [...fetchedCategories, ...apiCats];
        }
        setCategories(fetchedCategories);

        // 2. PRODUCT FETCH
        const productResponse = await fetch(`${API_DOMAIN}/product.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ search_text: "", action: "listProducts" }),
        });

        const productData = await productResponse.json();

        if (productData.head.code === 200) {
          let fetchedProducts = productData.body.products.map((product) => {
            const stock_control =
              product.stock_control !== undefined
                ? parseInt(product.stock_control)
                : 1;
            const stock_quantity =
              parseFloat(product.stock_quantity || product.quantity) || 0;
            const discount_control =
              product.discount_control !== undefined
                ? parseInt(product.discount_control)
                : 1;
            const discount = parseFloat(product.discount) || 0;
            const sales_rate = parseFloat(product.sales_rate) || 0;
            const sales_rate_with_discount_amount =
              parseFloat(product.sales_rate_with_discount_amount) || 0;

            let effective_price = sales_rate;
            let is_discounted = false;

            if (discount_control === 0 && discount > 0) {
              is_discounted = true;
              if (sales_rate_with_discount_amount > 0) {
                effective_price = sales_rate_with_discount_amount;
              } else {
                effective_price = sales_rate - sales_rate * (discount / 100);
              }
            }

            return {
              id: product.product_id,
              product_id: product.product_id,
              product_name: product.product_name,
              category: product.category,
              price: effective_price,
              original_price: sales_rate,
              is_discounted: is_discounted,
              sales_rate: sales_rate,
              purchase_rate: parseFloat(product.purchase_rate) || 0,
              discount_percent: discount_control === 0 ? discount : 0,
              discount_amount: sales_rate - effective_price,
              sales_rate_with_discount_amount: effective_price,
              product_mrp: parseFloat(product.product_mrp) || 0,
              discount: discount,
              discount_control: discount_control,
              stock_control: stock_control,
              stock_quantity: stock_quantity,
              product_img_url: product.product_img || "",
              unit: product.unit || "",
              barcode: product.barcode || "",
            };
          });
          setProducts(fetchedProducts);
        } else {
          throw new Error(productData.head.msg);
        }
      } catch (err) {
        setError("Could not load products/categories — " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsAndCategories();
  }, []);

  // ------------------ FILTERING ------------------
  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.product_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    let matchesCategory = false;

    if (selectedCategory === "all") {
      matchesCategory = true;
    } else if (selectedCategory === "discount") {
      matchesCategory = p.is_discounted;
    } else {
      matchesCategory = p.category === selectedCategory;
    }

    return matchesCategory && matchesSearch;
  });

  // ------------------ PAGINATION ------------------
  const totalItems = filteredProducts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handlePageChange = (p) => setCurrentPage(p);

  const cartTotalItems = cart.length;
  const cartTotalPrice = cart.reduce((sum, i) => sum + i.price * i.qty, 0);

  const handleProductClick = (product) => {
    navigate("/product-details", { state: { product } });
  };

  // ----------------------------------------------------
  return (
    <div className="home-section overflow-hidden">
      {/* ------------- ORDER TYPE MODAL ------------- */}
      <Modal
        show={orderMode === null}
        backdrop="static"
        keyboard={false}
        centered
        className="order-mode-modal"
      >
        <Modal.Header>
          <Modal.Title className="text-center w-100">Welcome</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="text-muted mb-4 text-center">
            Please choose how you'd like to order today.
          </p>

          <div className="order-mode-grid mb-4">
            {settingsLoading ? (
              <div
                className="text-center w-100 py-4"
                style={{ gridColumn: "1 / -1" }}
              >
                <Spinner animation="border" variant="primary" />
                <p className="small text-muted mt-2">Loading options...</p>
              </div>
            ) : websiteSettings &&
              Number(websiteSettings.table_order_enabled) !== 0 &&
              Number(websiteSettings.walking_customer_enabled) !== 0 &&
              Number(websiteSettings.home_delivery_enabled) !== 0 ? (
              <div
                className="text-center w-100 py-4"
                style={{ gridColumn: "1 / -1" }}
              >
                <div className="alert alert-warning border-0 rounded-4 shadow-sm">
                  <h6 className="fw-bold mb-1">Store Not Accepting Orders</h6>
                  <p className="small mb-0 opacity-75">
                    We're sorry, but online orders are currently disabled.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {websiteSettings &&
                  Number(websiteSettings.table_order_enabled) === 0 && (
                    <div
                      className={`order-mode-card ${tempOrderMode === 0 ? "active" : ""}`}
                      onClick={() => {
                        setTempOrderMode(0);
                        setFormError("");
                      }}
                    >
                      <span className="order-mode-icon">🪑</span>
                      <span className="order-mode-label">Table Order</span>
                    </div>
                  )}
                {websiteSettings &&
                  Number(websiteSettings.walking_customer_enabled) === 0 && (
                    <div
                      className={`order-mode-card ${tempOrderMode === 1 ? "active" : ""}`}
                      onClick={() => {
                        setTempOrderMode(1);
                        setFormError("");
                      }}
                    >
                      <span className="order-mode-icon">🚶</span>
                      <span className="order-mode-label">Walking Customer</span>
                    </div>
                  )}
                {websiteSettings &&
                  Number(websiteSettings.home_delivery_enabled) === 0 && (
                    <div
                      className={`order-mode-card ${tempOrderMode === 3 ? "active" : ""}`}
                      onClick={() => {
                        setTempOrderMode(3);
                        setFormError("");
                      }}
                    >
                      <span className="order-mode-icon">🏠</span>
                      <span className="order-mode-label">Home Delivery</span>
                    </div>
                  )}
              </>
            )}
          </div>

          {!settingsLoading &&
            websiteSettings &&
            tempOrderMode !== null &&
            ((tempOrderMode === 0 &&
              Number(websiteSettings.table_order_enabled) === 0) ||
              (tempOrderMode === 1 &&
                Number(websiteSettings.walking_customer_enabled) === 0) ||
              (tempOrderMode === 3 &&
                Number(websiteSettings.home_delivery_enabled) === 0)) && (
            <Form className="mt-3">
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your name"
                  value={tempUserDetails.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className="rounded-3"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">
                  Phone Number *
                </Form.Label>
                <Form.Control
                  type="tel"
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  value={tempUserDetails.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    handleInputChange("phone", val);
                    if (val.length === 10) setPhoneBlurError("");
                  }}
                  onBlur={(e) => {
                    if (e.target.value.length > 0 && e.target.value.length < 10) {
                      setPhoneBlurError("Phone number must be exactly 10 digits.");
                    } else {
                      setPhoneBlurError("");
                    }
                  }}
                  className={`rounded-3 ${phoneBlurError ? "is-invalid" : ""}`}
                />
                {phoneBlurError && (
                  <div className="text-danger small mt-1">{phoneBlurError}</div>
                )}
              </Form.Group>

              {tempOrderMode === 0 && (
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Table Number *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. T1, T2"
                    value={tempUserDetails.tableNo}
                    onChange={(e) =>
                      handleInputChange("tableNo", e.target.value)
                    }
                    className="rounded-3"
                  />
                </Form.Group>
              )}

              {tempOrderMode === 3 && (
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Delivery Address *
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Enter full address"
                    value={tempUserDetails.address}
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                    className="rounded-3"
                  />
                </Form.Group>
              )}

              {formError && (
                <p className="text-danger small mb-3">{formError}</p>
              )}

              <Button
                variant="primary"
                className="w-100 py-2 rounded-3 fw-bold mt-2"
                disabled={!isFormValid()}
                onClick={handleSubmitOrderType}
              >
                Continue
              </Button>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      <Container fluid>
        {/* ------------- COMPANY DETAILS ------------- */}
        {companyLoading ? (
          <p className="text-center">Loading company details...</p>
        ) : companyError ? (
          <Alert variant="danger">Error: {companyError}</Alert>
        ) : company ? (
          <>
            <div className="hero-section mb-5">
              <Container>
                {/* Desktop Layout (md and up) */}
                <Row className="align-items-center justify-content-center text-center text-md-start d-none d-md-flex">
                  <Col md={6} className="order-2 order-md-1 mt-4 mt-md-0">
                    <h1 className="hero-title fw-bold mb-3">
                      {company.company_name}
                    </h1>
                    <p className="hero-info text-muted mb-4">
                      <FaMapMarkerAlt className="me-2 text-primary" />
                      {company.address?.split("\n").map((line, i) => (
                        <span key={i}>{line} </span>
                      ))}
                    </p>
                    <div className="d-flex gap-3 justify-content-center justify-content-md-start flex-wrap">
                      <a
                        href={`tel:${company.mobile_number}`}
                        className="btn-hero btn-hero-primary d-flex align-items-center"
                      >
                        <FaPhoneAlt className="me-2" /> Call Store
                      </a>
                      {company.mobile_number && (
                        <a
                          href={`https://wa.me/${String(company.mobile_number).replace(/[^0-9]/g, "")}`}
                          className="btn-hero btn-hero-whatsapp d-flex align-items-center"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaWhatsapp className="me-2 fs-5" /> WhatsApp
                        </a>
                      )}
                    </div>
                  </Col>
                  <Col
                    md={6}
                    className="order-1 order-md-2 d-flex justify-content-center justify-content-md-end"
                  >
                    <div className="hero-logo-wrapper">
                      <img
                        src={
                          company.company_logo_url ||
                          "https://zenonlinestore.zentexus.in/uploads/company/2025-12-15-13-39-50.webp"
                        }
                        alt="Company Logo"
                        className="hero-logo shadow-lg"
                      />
                    </div>
                  </Col>
                </Row>

                {/* Mobile Layout (less than md) */}
                <div className="d-md-none">
                  <Row className="align-items-center">
                    <Col xs={7}>
                      <div className="mobile-address-section mb-3">
                        <p
                          className="hero-info text-muted mb-2 text-start"
                          style={{
                            fontSize: "13px",
                            lineHeight: "1.4",
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: "3",
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          <FaMapMarkerAlt className="me-1 text-primary" />
                          {company.address}
                        </p>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        <a
                          href={`tel:${company.mobile_number}`}
                          className="btn-hero btn-hero-primary py-2 px-3 d-flex align-items-center justify-content-center"
                          style={{ fontSize: "14px", whiteSpace: "nowrap" }}
                        >
                          <FaPhoneAlt className="me-2" /> Call Store
                        </a>
                        {company.mobile_number && (
                          <a
                            href={`https://wa.me/${String(company.mobile_number).replace(/[^0-9]/g, "")}`}
                            className="btn-hero btn-hero-whatsapp py-2 px-3 d-flex align-items-center justify-content-center"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: "14px", whiteSpace: "nowrap" }}
                          >
                            <FaWhatsapp className="me-2" /> WhatsApp
                          </a>
                        )}
                      </div>
                    </Col>
                    <Col xs={5} className="d-flex justify-content-end">
                      <div
                        className="hero-logo-wrapper p-0"
                        style={{ maxWidth: "120px" }}
                      >
                        <img
                          src={
                            company.company_logo_url ||
                            "https://zenonlinestore.zentexus.in/uploads/company/2025-12-15-13-39-50.webp"
                          }
                          alt="Company Logo"
                          className="hero-logo shadow-sm"
                          style={{
                            width: "100%",
                            height: "auto",
                            border: "8px solid white",
                            borderRadius: "12px",
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col xs={12} className="text-center">
                      <h1
                        className="hero-title fw-bold"
                        style={{ fontSize: "28px", marginBottom: "0" }}
                      >
                        {company.company_name}
                      </h1>
                    </Col>
                  </Row>
                </div>
              </Container>
            </div>
          </>
        ) : null}

        {/* ------------- BANNERS ------------- */}
        {!bannersLoading && banners.length > 0 && (
          <Row className="mb-4">
            <Col xs={12}>
              <div className="banner-slider-wrapper rounded-4 overflow-hidden shadow-sm">
                {banners.length === 1 ? (
                  <div className="banner-single-item">
                    <img
                      src={banners[0].img}
                      alt="Promotional Banner"
                      className="w-100 img-fluid banner-img"
                    />
                  </div>
                ) : (
                  <Carousel
                    interval={3000}
                    fade={false}
                    indicators={true}
                    controls={true}
                    className="banner-carousel"
                  >
                    {banners.map((banner) => (
                      <Carousel.Item key={banner.id}>
                        <img
                          src={banner.img}
                          alt={`Banner ${banner.id}`}
                          className="d-block w-100 banner-img"
                        />
                      </Carousel.Item>
                    ))}
                  </Carousel>
                )}
              </div>
            </Col>
          </Row>
        )}

        <hr />

        {/* ----------- SEARCH BAR ----------- */}
        <Row className="mb-3">
          <Col xs={12} md={12} lg={3}>
            <Form.Control
              type="text"
              placeholder="Search for an item"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </Col>
        </Row>

        {/* ----------- CATEGORY BUTTONS ----------- */}
        <Row className="mb-4">
          <div className="category-scroll-container">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={
                  selectedCategory === cat.id ? "danger" : "outline-secondary"
                }
                className="category-btn"
                onClick={() => {
                  setSelectedCategory(cat.id);
                  setCurrentPage(1);
                }}
              >
                {cat.name}
              </Button>
            ))}
          </div>
        </Row>

        {/* ----------- PRODUCT LOADING / ERROR ----------- */}
        {loading && (
          <div className="text-center my-5">
            <Spinner animation="border" />
            <p>Loading products…</p>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {/* ----------- PRODUCT GRID ----------- */}
        <Row className="product-grid">
          {!loading && !error && currentProducts.length === 0 && (
            <div className="text-center my-5 w-100">
              <h5 className="text-muted">No products available</h5>
            </div>
          )}
          {!loading &&
            !error &&
            currentProducts.map((prod) => {
              const cartItem = cart.find((i) => i.id === prod.id);
              const isOutOfStock =
                prod.stock_control === 0 && prod.stock_quantity <= 0;

              return (
                <Col
                  key={prod.id}
                  xs={6}
                  sm={4}
                  md={3}
                  lg={2}
                  className="mb-4 custom-col"
                >
                  <div className="product-card">
                    <div
                      className="product-image-wrapper position-relative"
                      onClick={() => {
                        if (!isOutOfStock) handleProductClick(prod);
                      }}
                      style={{
                        cursor: isOutOfStock ? "not-allowed" : "pointer",
                      }}
                    >
                      {prod.is_discounted && prod.discount > 0 && (
                        <span className="discount-badge-new">
                          {prod.discount}% OFF
                        </span>
                      )}
                      {isOutOfStock && (
                        <div className="out-of-stock-overlay">
                          <span>OUT OF STOCK</span>
                        </div>
                      )}
                      <img
                        src={
                          prod.product_img_url ||
                          "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT7r0TBgU8-SuHKlgfTmNzdQoMrk3nU5tFarg&s"
                        }
                        alt={prod.product_name}
                        className="product-img"
                        style={{ opacity: isOutOfStock ? 0.4 : 1 }}
                      />
                    </div>

                    <div className="product-details text-center">
                      <p className="product-name mb-1">{prod.product_name}</p>

                      <div className="product-price-line justify-content-center mb-2">
                        {prod.is_discounted ? (
                          <>
                            <span className="current-price">
                              ₹{prod.price.toFixed(2)}
                              <small
                                className="text-muted ms-1"
                                style={{ fontSize: "11px" }}
                              >
                                / {prod.unit || "unit"}
                              </small>
                            </span>
                            <span className="old-price">
                              ₹{prod.original_price}
                            </span>
                          </>
                        ) : (
                          <span className="current-price">
                            ₹{prod.price.toFixed(2)}
                            <small
                              className="text-muted ms-1"
                              style={{ fontSize: "11px" }}
                            >
                              / {prod.unit || "unit"}
                            </small>
                          </span>
                        )}
                      </div>

                      <div className="d-flex justify-content-center w-100">
                        {isOutOfStock ? (
                          <div style={{ height: "35px" }}></div>
                        ) : cartItem !== undefined ? (
                          <div className="quantity-controls-pill">
                            <button
                              className="qty-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                const currentQty =
                                  parseFloat(cartItem.qty) || 0;
                                if (currentQty <= 1) {
                                  removeItem(prod.id);
                                } else {
                                  decreaseQty(prod.id);
                                }
                              }}
                            >
                              −
                            </button>
                            <input
                              type="number"
                              step="any"
                              className="qty-input"
                              value={cartItem.qty}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateQty(prod.id, e.target.value);
                              }}
                            />
                            <button
                              className="qty-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                increaseQty(prod.id);
                              }}
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="primary"
                            className="w-100 add-btn-clean"
                            style={{ maxWidth: "100px" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCart(prod);
                            }}
                          >
                            + Add
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
        </Row>

        {/* ----------- PAGINATION ----------- */}
        {totalItems > itemsPerPage && (
          <Row className="justify-content-center mt-4">
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </Row>
        )}

        {/* ----------- CART BAR ----------- */}
        {cart.length > 0 && (
          <div className="cart-bar">
            <div
              className="cart-info-clickable"
              onClick={() => navigate("/orderpage")}
              style={{ cursor: "pointer" }}
            >
              <span>{cartTotalItems} ITEMS</span>
              <span className="separator"> . </span>
              <span>₹ {cartTotalPrice.toFixed(2)}</span>
            </div>
            <div className="cart-action position-relative">
              <span
                className="cart-icon"
                onClick={() => navigate("/orderpage")}
                style={{ cursor: "pointer" }}
              >
                🛒
              </span>

              {cartTotalItems > 0 && (
                <span className="cart-badge">{cartTotalItems}</span>
              )}

              <Button onClick={() => navigate("/orderpage")}>View cart</Button>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default Home;
